import { Component } from "@angular/core";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { formatDecimalNumbersInString } from "@core/currency-display.util";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import * as _ from "lodash";
import { Observable, from, of, throwError } from "rxjs";
import { catchError, mergeMap } from "rxjs/operators";
import { StoreRepositoryService } from "../store-repository.service";
import { IonicModule } from "@ionic/angular";
import { NgStyle, AsyncPipe } from "@angular/common";

@Component({
    selector: "app-pick-an-offer",
    templateUrl: "./pick-an-offer.component.html",
    styleUrls: ["./pick-an-offer.component.scss"],
    standalone: true,
    imports: [
        IonicModule,
        NgStyle,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class PickOfferComponent {
  public offerArray: Data[] = [];
  public languageCode = "en";
  public currencySymbol = "£";
  public preferredCurrency = "GBP";
  public selectedOfferIndex = 0;
  public offerData$: Observable<Data> = this.authService
    .getAngularFireStore("/general_modules/custom_offer/list/EuroMillions", "get")
    .pipe(
      catchError((error) => {
        return throwError(error);
      }),
      mergeMap((res: Data) => {
        if (!res) return of(undefined); // Return an empty observable if response is falsy
        const clonedData = _.cloneDeep(res.data());

        if (clonedData?.offer) {
          this.offerArray = this.orderedKeys.map((key) => {
            return { id: key, ...clonedData.offer[key] };
          });
        }

        const modifyAsyncTasks = Object.keys(clonedData.content[this.languageCode]).map((key) => {
          if (key.includes("week") || key.includes("subscription")) {
            const description = this.getModifiedDvString(clonedData.content[this.languageCode][key].description);
            const title = this.getModifiedDvString(clonedData.content[this.languageCode][key].title);

            // Perform asynchronous tasks concurrently using Promise.all
            return Promise.all([this.authService.getDVFromString(description), this.authService.getDVFromString(title)])
              .then(([descriptionResult, titleResult]) => {
                if (descriptionResult && typeof descriptionResult === "string") {
                  clonedData.content[this.languageCode][key].description =
                    this.formatNumbersInString(descriptionResult);
                }

                if (titleResult) {
                  clonedData.content[this.languageCode][key].title = titleResult;
                  clonedData.content[this.languageCode][key].titleBold = this.formatNumbersInString(
                    clonedData.content[this.languageCode][key].title.split(" ")[this.digitZero]
                  );
                  clonedData.content[this.languageCode][key].titleRegular = clonedData.content[this.languageCode][
                    key
                  ].title
                    .split(" ")
                    .slice(1)
                    .join(" ");
                }
              })
              .catch((err) => {
                throw new Error(`Error from getDVFromString ${err}`);
              });
          }
        });

        // Wait for all asynchronous tasks to complete before emitting the modified data
        return from(modifyAsyncTasks).pipe(mergeMap(() => of(clonedData)));
      })
    );

  private orderedKeys = ["1_week", "2_weeks", "4_weeks", "8_weeks", "subscription"];
  private digitZero = 0;

  constructor(
    private router: Router,
    private authService: AuthService,
    private storeService: StoreRepositoryService,
    public translate: TranslateService
  ) {
    this.authService.selectedlanguage$.subscribe((l) => {
      this.languageCode = l ? l : "en";
      translate.use(l ? l : "en");
    });

    this.authService.userDetails$.subscribe(
      (user: Data) => {
        this.languageCode = user?.language_code ? user?.language_code : "en";
        this.preferredCurrency = user?.preferred_currency ? user?.preferred_currency : "GBP";
        translate.use(this.languageCode ? this.languageCode : "en");
        this.authService.getCurrencyList$.subscribe((data: Data) => {
          const CurrencyDetails = data?.details?.find((x: Data) => x?.currency_code === this.preferredCurrency);
          this.currencySymbol = CurrencyDetails?.currency_symbol ? CurrencyDetails?.currency_symbol : "£";
        });
      },
      (error) => {
        return error;
      }
    );
  }

  public closeDialog(): void {
    this.router.navigateByUrl("/mainTabs/tabs/home");
  }

  public joinOffer(data: Data): void {
    const creditBackCheck =
      this.offerArray[this.selectedOfferIndex]?.credit_back &&
      typeof this.offerArray[this.selectedOfferIndex]?.credit_back === "number" &&
      this.offerArray[this.selectedOfferIndex]?.credit_back > this.digitZero;
    const cashbackContent =
      data?.content?.[this.languageCode]?.[this.offerArray[this.selectedOfferIndex].id]?.description;
    this.storeService.customOfferCashBackText$.next(creditBackCheck ? this.currencySymbol + cashbackContent : "");
    const offerId = this.offerArray[this.selectedOfferIndex].offerid;
    localStorage.setItem("custom-offer-toast", creditBackCheck ? "true" : "false");
    const awardId = this.offerArray[this.selectedOfferIndex]?.award_id ?? this.digitZero;
    this.storeService.customOfferAwardId$.next(creditBackCheck ? awardId : this.digitZero);
    this.router.navigateByUrl("/store?payment=" + offerId);
  }

  public selectOffer(index: number): void {
    this.selectedOfferIndex = index;
  }

  public formatNumbersInString(originalString: string): string {
    return formatDecimalNumbersInString(originalString);
  }

  private getModifiedDvString(dvString: string): string {
    let tempString = dvString;
    const placeholderRegex = /\[currency_node:(.*?)\]/;
    const match = tempString.match(placeholderRegex);

    // If a placeholder is found, extract the offerid and property name, and find the corresponding offer object
    if (match) {
      const [placeholder, offerProperty] = match[1].split("."); // Extract offerid and property name
      const offer = this.offerArray.find((o) => {
        if (placeholder == "oneoff") {
          return o.id === "1_week";
        } else {
          return o.id === placeholder;
        }
      });

      let propertyValue = 0;

      // condition to allow access to specific properties only
      // to fix 'Generic Object Injection Sink' issue
      const allowedProperties = [
        "full_price",
        "int_full_price",
        "credit_back",
        "int_credit_back",
        "price",
        "int_price",
      ];

      if (offer && offer[offerProperty]) {
        if (offer["int_" + offerProperty] && allowedProperties.includes("int_" + offerProperty)) {
          propertyValue = offer["int_" + offerProperty][this.preferredCurrency];
        } else if (allowedProperties.includes(offerProperty)) {
          propertyValue = offer[offerProperty];
        }
        tempString = tempString.replace(match[this.digitZero], propertyValue.toString());
      } else {
        tempString = tempString.replace(match[this.digitZero], "No");
      }
    }
    return tempString;
  }
}
